import { GoogleAccountsId, GoogleCredentialResponse } from '../types/googleIdentity';

/**
 * Google Identity Services, loaded on demand.
 *
 * The script is fetched the first time a sign-in button mounts and
 * `google.accounts.id.initialize` runs once for the page: Google keeps a
 * single configuration, so the one callback registered here forwards to
 * whichever button is currently on screen.
 *
 * The client id is a public identifier, not a secret.
 */
export const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '669000288849-1k52co4be4j7tp7fr5euqnnjstpq7a4a.apps.googleusercontent.com';

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

type CredentialHandler = (credential: string) => void;

let loading: Promise<GoogleAccountsId> | null = null;
let handler: CredentialHandler | null = null;

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Blocked or offline. Remove the tag so a later attempt starts clean.
      script.remove();
      reject(new Error('Google sign-in could not be loaded'));
    };
    document.head.appendChild(script);
  });
}

/**
 * Resolves with `google.accounts.id`, initialized for this app. Rejects when
 * the script cannot be loaded (blocked by an extension, offline); the caller
 * then simply shows no Google button. A failed load is retried the next time
 * a button mounts.
 */
export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  if (!loading) {
    loading = loadScript()
      .then(() => {
        const id = window.google?.accounts?.id;
        if (!id) throw new Error('Google sign-in is unavailable');
        id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          ux_mode: 'popup',
          callback: (response: GoogleCredentialResponse) => {
            if (response.credential) handler?.(response.credential);
          },
        });
        return id;
      })
      .catch((error) => {
        loading = null;
        throw error;
      });
  }
  return loading;
}

/**
 * Route Google's credential callback to `next`. Returns a function that
 * releases it, which does nothing if another button has taken over since.
 */
export function onGoogleCredential(next: CredentialHandler): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
}
